// CreateLeadModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, setUser } from "../../store/authSlice";
import API from "../../api";

/**
 * CreateLeadModal
 * - 3 step wizard: 1) User Details  2) Business Details  3) Call Details
 * - Step headers are clickable (user can jump forward/back)
 * - Required fields show * mark
 * - Validation ONLY on final submit; if errors, inputs turn red + banner shown
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onCreated?: (createdLead?: any) => void
 *   endpoint?: string                // default '/auth/createLead'
 */
export default function CreateLeadModal({
  open,
  onClose,
  onCreated,
  endpoint = "/auth/createLead",
}) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [bannerOk, setBannerOk] = useState("");
  const [bannerErr, setBannerErr] = useState("");

  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  // NEW: validation state (only populated on final submit)
  const [errors, setErrors] = useState({});
  const [didSubmitAttempt, setDidSubmitAttempt] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!user) {
          const { data } = await API.get("/auth/me");
          if (!ignore && data?.user) dispatch(setUser(data.user));
        }
      } catch (_) {}
    })();
    return () => {
      ignore = true;
    };
  }, [user, dispatch]);

  const [form, setForm] = useState({
    // Step 1 — user details
    name: "",
    email: "",
    mobile: "",
    state: "",
    address: "",

    // Step 2 — business details
    bname: "",
    monthlyProcessingFee: "", // maps to DB column `value`
    isReprogram: false,

    // Step 3 — call details
    date: "",
    time: "",
    tmzone: "",
    sendDefaultMail: "yes",
    comment: "",
    sentMail: "free_pos", // default
  });

  const feeOptions = useMemo(
    () => ["<$5k", "$5k–$25k", "$25k–$100k", ">$100k"],
    []
  );
  const timeZoneOptions = useMemo(() => ["est", "cst", "mst", "pst", "hst"], []);
  const yesNo = useMemo(() => ["yes", "no"], []);

  if (!open) return null;

  function update(field) {
    return (e) =>
      setForm((f) => ({
        ...f,
        [field]: e?.target?.value ?? e,
      }));
  }

  // ---------- NEW: UI helpers ----------
  const steps = [
    { key: "user", label: "User Details" },
    { key: "biz", label: "Business Details" },
    { key: "call", label: "Call Details" },
  ];

  const requiredMark = (
    <span style={{ color: "#ef4444", marginLeft: 4, fontWeight: 800 }}>*</span>
  );

  // style helper: red border only after final submit attempt
  const fieldStyle = (field) => {
    if (!didSubmitAttempt) return undefined;
    if (!errors?.[field]) return undefined;
    return {
      borderColor: "#ef4444",
      outlineColor: "#ef4444",
      boxShadow: "0 0 0 2px rgba(239,68,68,0.15)",
    };
  };

  // ---------- NEW: validation (only used on FINAL submit) ----------
  function validateAll(nextForm) {
    const e = {};

    // Step 1 required
    if (!nextForm.name?.trim()) e.name = "Name is required";
    if (!nextForm.mobile?.trim()) e.mobile = "Mobile is required";

    // Step 2 required
    if (!nextForm.bname?.trim()) e.bname = "Business name is required";
    if (!nextForm.monthlyProcessingFee) e.monthlyProcessingFee = "Monthly fee is required";

    // Step 3 required
    if (!nextForm.date) e.date = "Call date is required";
    if (!nextForm.time) e.time = "Call time is required";
    if (!nextForm.tmzone) e.tmzone = "Time zone is required";

    return e;
  }

  function firstErrorStep(e) {
    // map fields to steps: 0 user, 1 biz, 2 call
    const step0 = ["name", "mobile", "email", "state", "address"];
    const step1 = ["bname", "monthlyProcessingFee"];
    const step2 = ["date", "time", "tmzone", "comment", "sendDefaultMail", "sentMail"];

    const keys = Object.keys(e || {});
    if (keys.some((k) => step0.includes(k))) return 0;
    if (keys.some((k) => step1.includes(k))) return 1;
    if (keys.some((k) => step2.includes(k))) return 2;
    return 0;
  }

  // Step navigation: NO validation here (per your requirement)
  function next() {
    setBannerErr("");
    setBannerOk("");
    setStep((s) => Math.min(2, s + 1));
  }

  function back() {
    setBannerErr("");
    setBannerOk("");
    setStep((s) => Math.max(0, s - 1));
  }

  function goToStep(i) {
    if (submitting) return;
    setBannerErr("");
    setBannerOk("");
    setStep(i);
  }

  // CreateLeadModal.jsx
  function extractApiError(err) {
    const res = err?.response;
    if (res) {
      const { status, data } = res;
      const apiMsg = data?.message || data?.error;
      if (status === 409) {
        const leadIdSuffix = data?.leadId ? ` (Existing lead ID: ${data.leadId})` : "";
        return (
          apiMsg ||
          ("Duplicate lead: A lead with this business name and mobile already exists." +
            leadIdSuffix)
        );
      }
      if (apiMsg) return apiMsg;
    }
    if (err?.message?.toLowerCase()?.includes("network"))
      return "Network error — please check your connection and try again.";
    return "Failed to create lead. Please try again.";
  }

  async function submit() {
    setBannerErr("");
    setBannerOk("");

    // ✅ validate ONLY here (final submit)
    setDidSubmitAttempt(true);
    const e = validateAll(form);
    setErrors(e);

    if (Object.keys(e).length > 0) {
      setBannerErr("Please fill all required fields.");
      setStep(firstErrorStep(e)); // jump user to the step with the first error
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: form.name || "",
        bname: form.bname || "",
        email: form.email || "",
        mobile: form.mobile || "",
        value: form.monthlyProcessingFee || "",
        agent: user?.username || user?.name || "",
        ass: user?.ass ?? "",
        calldate: form.date || null,
        comment: form.comment || "",
        status: "interested",
        aid: user?.id ?? "",
        tmzone: form.tmzone || "",
        address: form.address || "",
        calltime: form.time || "",
        state: form.state || "",
        timestampp: new Date(),
        role: user?.role || "",
        userEmail: user?.email || "",
        sentMail: form.sentMail || "free_pos",
        isReprogram: form.isReprogram ? 1 : 0,
      };

      const resp = await API.post(endpoint, payload);
      const data = resp?.data || {};

      setBannerOk("Lead created.");
      setTimeout(() => setBannerOk(""), 3000);
      // Email / SMS / Instantly run in background via leadPoller.
      // Any failures will appear as toasts automatically via socket event.
      onCreated?.(data || payload);

      // reset
      setForm({
        name: "",
        email: "",
        mobile: "",
        state: "",
        address: "",
        bname: "",
        monthlyProcessingFee: "",
        isReprogram: false,
        date: "",
        time: "",
        tmzone: "",
        sendDefaultMail: "no",
        comment: "",
        sentMail: "free_pos",
      });

      setErrors({});
      setDidSubmitAttempt(false);
      setStep(0);
      onClose?.();
    } catch (e2) {
      console.error(e2);
      const msg = extractApiError(e2);
      setBannerErr(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ldm-backdrop" onClick={onClose} role="presentation">
      <div
        className="ldm-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="ldm-header">
          <h3>Create Lead</h3>
          <div className="ldm-actions">
            {bannerErr && (
              <span className="ldm-banner ldm-banner-error">{bannerErr}</span>
            )}
            {bannerOk && <span className="ldm-banner ldm-banner-ok">{bannerOk}</span>}
            <button className="ldm-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="ldm-body">
          {/* Stepper (clickable step labels) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 18,
              marginBottom: 12,
              padding: "0 2px",
              flexWrap: "wrap",
            }}
          >
            {steps.map((s, i) => {
              const active = i === step;
              const done = i < step;

              return (
                <div
                  key={s.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: done ? 0.85 : 1,
                    cursor: submitting ? "not-allowed" : "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => goToStep(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") goToStep(i);
                  }}
                  aria-current={active ? "step" : undefined}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "999px",
                      background: active || done ? "#3b82f6" : "#e5e7eb",
                      color: active || done ? "#fff" : "#374151",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </div>

                  <div
                    style={{
                      fontWeight: 700,
                      color: active ? "#111827" : "#6b7280",
                      textDecoration: active ? "underline" : "none",
                    }}
                  >
                    {s.label}
                  </div>

                  {i < steps.length - 1 && (
                    <div
                      style={{
                        width: 36,
                        height: 2,
                        background: i < step ? "#3b82f6" : "#e5e7eb",
                        borderRadius: 999,
                        margin: "0 8px",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          {step === 0 && (
            <div className="ldm-grid" style={{ marginTop: 8 }}>
              <div className="ldm-field">
                <label>
                  Name {requiredMark}
                </label>
                <input
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Full name"
                  style={fieldStyle("name")}
                />
              </div>

              <div className="ldm-field">
                <label>
                  Mobile Number {requiredMark}
                </label>
                <input
                  value={form.mobile}
                  onChange={update("mobile")}
                  placeholder="Mobile"
                  style={fieldStyle("mobile")}
                />
              </div>

              <div className="ldm-field">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Email"
                  style={fieldStyle("email")}
                />
              </div>

              <div className="ldm-field">
                <label>State</label>
                <input
                  value={form.state}
                  onChange={update("state")}
                  placeholder="State"
                  style={fieldStyle("state")}
                />
              </div>

              <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                <label>Address</label>
                <input
                  value={form.address}
                  onChange={update("address")}
                  placeholder="Address"
                  style={fieldStyle("address")}
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="ldm-grid" style={{ marginTop: 8 }}>
              <div className="ldm-field">
                <label>
                  Business Name {requiredMark}
                </label>
                <input
                  value={form.bname}
                  onChange={update("bname")}
                  placeholder="Business name"
                  style={fieldStyle("bname")}
                />
              </div>

              <div className="ldm-field">
                <label>
                  Monthly Processing Fee {requiredMark}
                </label>
                <select
                  value={form.monthlyProcessingFee}
                  onChange={update("monthlyProcessingFee")}
                  style={fieldStyle("monthlyProcessingFee")}
                >
                  <option value="" disabled>
                    Select monthly fee
                  </option>
                  {feeOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    cursor: "pointer",
                    userSelect: "none",
                    fontWeight: 600,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.isReprogram}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isReprogram: e.target.checked }))
                    }
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                  New Machine / Reprogram
                </label>
                {form.isReprogram && (
                  <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
                    Reprogram lead: only a thank-you SMS will be sent. No pitch email.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="ldm-grid" style={{ marginTop: 8 }}>
              <div className="ldm-field">
                <label>
                  Call Date {requiredMark}
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={update("date")}
                  style={fieldStyle("date")}
                />
              </div>

              <div className="ldm-field">
                <label>
                  Call Time {requiredMark}
                </label>
                <input
                  type="time"
                  value={form.time}
                  onChange={update("time")}
                  style={fieldStyle("time")}
                />
              </div>

              <div className="ldm-field">
                <label>
                  Time Zone {requiredMark}
                </label>
                <select
                  value={form.tmzone}
                  onChange={update("tmzone")}
                  style={fieldStyle("tmzone")}
                >
                  <option value="" disabled>
                    Select time zone
                  </option>
                  {timeZoneOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ldm-field">
                <label style={{ fontWeight: 600, marginTop: 8 }}>
                  Send default email
                </label>
                <select
                  value={form.sentMail}
                  onChange={(e) => setForm((f) => ({ ...f, sentMail: e.target.value }))}
                >
                  <option value="no_fee">No Fee (default)</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="flat_fee">Flat Fee</option>
                </select>
              </div>

              <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                <label>Comment</label>
                <textarea
                  rows={3}
                  value={form.comment}
                  onChange={update("comment")}
                  placeholder="Add a note…"
                  style={fieldStyle("comment")}
                />
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            {step > 0 && (
              <button className="ldm-btn" onClick={back} disabled={submitting}>
                Back
              </button>
            )}

            {step < 2 && (
              <button
                className="ldm-btn ldm-btn-primary"
                onClick={next}
                disabled={submitting}
              >
                Next
              </button>
            )}

            {step === 2 && (
              <button
                className="ldm-btn ldm-btn-primary"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? "Creating…" : "Create Lead"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
